import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/** An inclusive span of whole months, as `YYYY-MM` strings. */
export interface MonthRange {
  start: string | null;
  end: string | null;
}

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** `YYYY-MM` → a sortable integer, so comparisons don't go via Date. */
const ord = (m: string | null): number | null => {
  if (!m) return null;
  const [y, mo] = m.split("-").map(Number);
  return y != null && mo != null ? y * 12 + (mo - 1) : null;
};

const key = (year: number, monthIdx: number): string =>
  `${year}-${String(monthIdx + 1).padStart(2, "0")}`;

export const formatMonth = (m: string | null): string => {
  if (!m) return "…";
  const [y, mo] = m.split("-").map(Number);
  if (y == null || mo == null) return m;
  return `${MONTHS_SHORT[mo - 1]} ${y}`;
};

/** The current month as `YYYY-MM`, in LOCAL time.
 *  `toISOString().slice(0, 7)` is the trap this exists to avoid: on the 1st of
 *  the month, anywhere west of UTC, it returns the previous month. */
export const currentMonth = (d: Date = new Date()): string =>
  key(d.getFullYear(), d.getMonth());

/** Shift a `YYYY-MM` by n months. */
export const addMonths = (m: string, n: number): string => {
  const o = ord(m);
  if (o == null) return m;
  const next = o + n;
  return key(Math.floor(next / 12), next % 12);
};

/**
 * Month-granularity range picker — the accounting sibling of DateRangePicker.
 *
 * Accounting periods are whole months: a roll-up "for August" means the whole
 * of August, and asking someone to pick Aug 1 – Aug 31 on a day calendar makes
 * them do arithmetic to express something they already knew. It also invites
 * the off-by-one nobody catches (Aug 1 – Aug 30, and the last day quietly
 * leaves the books).
 *
 * Click once to set the start, again to set the end; clicking a month before
 * the start restarts the range there rather than producing an inverted one.
 */
export function MonthRangePicker({
  value,
  onChange,
  className,
  id,
  align = "start",
}: {
  value: MonthRange;
  onChange: (next: MonthRange) => void;
  className?: string;
  id?: string;
  align?: "start" | "center" | "end";
}) {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(() => {
    const from = value.start ?? value.end ?? currentMonth();
    return Number(from.slice(0, 4));
  });
  // Which end of the range the next click sets.
  const [picking, setPicking] = useState<"start" | "end">("start");
  const [hover, setHover] = useState<string | null>(null);

  const startOrd = ord(value.start);
  const endOrd = ord(value.end);
  const hoverOrd = ord(hover);
  // While choosing the end, preview the span the cursor is over.
  const previewEnd = picking === "end" && endOrd == null ? hoverOrd : endOrd;

  const pick = (month: string) => {
    const picked = ord(month)!;
    if (picking === "start" || startOrd == null || picked < startOrd) {
      onChange({ start: month, end: null });
      setPicking("end");
      return;
    }
    onChange({ start: value.start, end: month });
    setPicking("start");
    setOpen(false);
  };

  const label =
    value.start || value.end
      ? `${formatMonth(value.start)} – ${formatMonth(value.end)}`
      : "All months";

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Reopening always starts a fresh range rather than resuming a
        // half-finished one from last time.
        if (next) setPicking("start");
        else setHover(null);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          className={cn("h-9 justify-start font-normal", className)}
        >
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-64 p-3">
        <div className="mb-2 flex items-center justify-between">
          <button
            type="button"
            aria-label="Previous year"
            onClick={() => setYear((y) => y - 1)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-medium tabular-nums">{year}</span>
          <button
            type="button"
            aria-label="Next year"
            onClick={() => setYear((y) => y + 1)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-1" onMouseLeave={() => setHover(null)}>
          {MONTHS_SHORT.map((name, idx) => {
            const month = key(year, idx);
            const o = ord(month)!;
            const isStart = startOrd === o;
            const isEnd = endOrd === o;
            const inRange =
              startOrd != null &&
              previewEnd != null &&
              o > Math.min(startOrd, previewEnd) &&
              o < Math.max(startOrd, previewEnd);
            return (
              <button
                key={month}
                type="button"
                onClick={() => pick(month)}
                onMouseEnter={() => setHover(month)}
                aria-pressed={isStart || isEnd}
                className={cn(
                  "rounded-md py-1.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  isStart || isEnd
                    ? "bg-brand font-medium text-white"
                    : inRange
                      ? "bg-brand-light text-brand"
                      : "hover:bg-accent",
                )}
              >
                {name}
              </button>
            );
          })}
        </div>

        <div className="mt-2 flex items-center justify-between text-[11px]">
          <span className="text-muted-foreground">
            {picking === "end" && value.start
              ? `Ends after ${formatMonth(value.start)}…`
              : label}
          </span>
          {(value.start || value.end) && (
            <button
              type="button"
              onClick={() => {
                onChange({ start: null, end: null });
                setPicking("start");
              }}
              className="text-brand hover:underline focus-visible:outline-none focus-visible:underline"
            >
              Clear
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
